import React, { useState, useEffect } from 'react';
import LoadingButton from './LoadingButton';
import CalculationProgress from './CalculationProgress';
import './SupplyMapSection.css';
import { useTokenOps } from '../../../components/TokenContext';
import VersionSkeleton from '../../../components/VersionSkeleton';
import TraceabilityVersionValue from './TraceabilityVersionValue';
import { hostname } from '../../../providers/api';
import {
  LcaVersionStatusIcon,
  lastLcaVersionLabel,
} from '../../../components/LcaVersionStatus';

const SupplyMapSection = ({
  record,
  downloadBackup,
  runCalculation,
  loadingDownload,
  loadingCalculation,
  isCalculateable,
  calculationStatus,
  downloadStatus,
  otherCalculationBusy,
  pendingBcomeVersionId,
}) => {
  const tokenOps = useTokenOps();
  const hasCompletedRun = calculationStatus?.label === 'Completed';
  const [lastCalculationDate, setLastCalculationDate] = useState(null);
  const [loadingDate, setLoadingDate] = useState(true);
  const [lcaVersionStatus, setLcaVersionStatus] = useState(null);
  const [loadingLcaVersion, setLoadingLcaVersion] = useState(true);

  useEffect(() => {
    if (record?.id) {
      setLoadingDate(true);
      fetch(`${hostname}/lca-supply/last-calculation/${record.id}`, {
        headers: {
          authorization: 'Bearer ' + tokenOps.account?.accessToken,
        },
      })
        .then((response) => {
          if (!response.ok) {
            return null;
          }
          return response.json();
        })
        .then((data) => {
          if (
            data &&
            data.lastCalculation &&
            data.lastCalculation.runTimestamp
          ) {
            setLastCalculationDate(data.lastCalculation.runTimestamp);
          }
        })
        .catch((error) => {
          console.log('Error fetching last calculation date:', error);
        })
        .finally(() => setLoadingDate(false));
    }
  }, [record?.id, tokenOps.account?.accessToken, hasCompletedRun]);

  // updatedAt changes after a rollback or a manual LCA upload (record refresh).
  useEffect(() => {
    if (!record?.id) return;
    let cancelled = false;
    setLoadingLcaVersion(true);
    fetch(`${hostname}/lca-supply/lca-version-status/${record.id}`, {
      headers: {
        authorization: 'Bearer ' + tokenOps.account?.accessToken,
      },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled) setLcaVersionStatus(data);
      })
      .catch((error) => {
        console.log('Error fetching LCA version status:', error);
      })
      .finally(() => {
        if (!cancelled) setLoadingLcaVersion(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    record?.id,
    record?.updatedAt,
    tokenOps.account?.accessToken,
    hasCompletedRun,
  ]);

  return (
    <>
      <div className="supply-map-section__divider"></div>

      <div className="supply-map-section">
        <h3 className="supply-map-section__title">
          Supply map, LCA and water consumption
        </h3>
        <div className="supply-map-section__content">
          <div className="supply-map-section__field">
            <strong className="supply-map-section__label">
              Last calculation
            </strong>
            <div className="supply-map-section__value">
              {loadingDate
                ? 'Loading...'
                : lastCalculationDate
                ? new Date(lastCalculationDate).toLocaleDateString('es-ES')
                : 'N/A'}
            </div>
          </div>
          <div className="supply-map-section__field">
            <strong className="supply-map-section__label">
              Traceability version
            </strong>
            <div className="supply-map-section__value">
              <TraceabilityVersionValue
                record={record}
                pendingBcomeVersionId={pendingBcomeVersionId}
                emptyLabel="N/A"
              />
            </div>
          </div>
          <div className="supply-map-section__field">
            <strong className="supply-map-section__label">
              Last LCA version
            </strong>
            <div className="supply-map-section__value supply-map-section__value--with-status">
              {loadingLcaVersion ? (
                <VersionSkeleton />
              ) : (
                <>
                  {lastLcaVersionLabel(lcaVersionStatus)}
                  {/* The comparison is against the project's current version: not
                      meaningful until the upload in progress finishes. */}
                  {!pendingBcomeVersionId && (
                    <LcaVersionStatusIcon lcaVersionStatus={lcaVersionStatus} />
                  )}
                </>
              )}
            </div>
          </div>
          <div>
            {!loadingDownload && (
              <LoadingButton
                color="primary"
                onClick={() => downloadBackup(record)}
                disabled={loadingCalculation || otherCalculationBusy}
              >
                Download
              </LoadingButton>
            )}
            <CalculationProgress
              status={
                downloadStatus ||
                (loadingDownload
                  ? { label: 'Requesting export', percent: null }
                  : null)
              }
            />
          </div>
          <div>
            {!loadingCalculation && (
              <LoadingButton
                color="primary"
                variant="contained"
                onClick={runCalculation}
                disabled={
                  !isCalculateable || loadingDownload || otherCalculationBusy
                }
              >
                Run calculation
              </LoadingButton>
            )}
            <CalculationProgress
              status={
                calculationStatus ||
                (loadingCalculation
                  ? { label: 'Submitting request', percent: null }
                  : null)
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SupplyMapSection;
